import React, { useState, useRef, useCallback, useEffect } from "react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * JarvisButton — Floating AI assistant with voice interaction.
 * Optimized: TTS starts on first sentence while rest streams.
 */
export default function JarvisButton({ serviceId }) {
    const [state, setState] = useState("idle");
    const [active, setActive] = useState(false);
    const audioQueueRef = useRef([]);
    const isPlayingRef = useRef(false);
    const currentAudioRef = useRef(null);
    const recognitionRef = useRef(null);
    const abortRef = useRef(null);
    const stateRef = useRef("idle");

    // Keep stateRef in sync
    useEffect(() => { stateRef.current = state; }, [state]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopEverything();
        };
    }, []); // cleanup on unmount

    const getToken = () => localStorage.getItem("trulyn_token");

    const stopEverything = () => {
        if (recognitionRef.current) {
            try { recognitionRef.current.abort(); } catch (_e) { /* cleanup */ }
            recognitionRef.current = null;
        }
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
        }
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch (_e) { /* cleanup */ }
        }
        audioQueueRef.current = [];
        isPlayingRef.current = false;
    };

    // ── Audio queue player ──
    const playNextInQueue = useCallback(async () => {
        if (isPlayingRef.current || audioQueueRef.current.length === 0) {
            // Queue empty and done playing → start listening
            if (!isPlayingRef.current && audioQueueRef.current.length === 0 && stateRef.current === "speaking") {
                startListening();
            }
            return;
        }
        isPlayingRef.current = true;
        setState("speaking");

        const audioBytes = audioQueueRef.current.shift();
        try {
            const blob = new Blob([audioBytes], { type: "audio/mpeg" });
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            currentAudioRef.current = audio;

            await new Promise((resolve, reject) => {
                audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
                audio.onerror = () => { URL.revokeObjectURL(url); reject(); };
                audio.play().catch(reject);
            });
        } catch (_e) { /* audio play error */ }

        currentAudioRef.current = null;
        isPlayingRef.current = false;
        playNextInQueue();
    }, []); // cleanup on unmount

    const enqueueTTS = useCallback(async (text) => {
        if (!text.trim()) return;
        const token = getToken();
        try {
            const resp = await fetch(`${API_BASE}/api/ai/jarvis/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ text }),
            });
            if (!resp.ok) return;
            const audioBytes = await resp.arrayBuffer();
            audioQueueRef.current.push(audioBytes);
            playNextInQueue();
        } catch (_e) { /* TTS error */ }
    }, [playNextInQueue]);

    // ── Speech Recognition ──
    const startListening = useCallback(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            toast.error("Tu navegador no soporta reconocimiento de voz");
            setState("idle");
            setActive(false);
            return;
        }

        setState("listening");
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        recognition.lang = "es-SV";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.continuous = false;

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            if (transcript.trim()) {
                sendMessage(transcript);
            }
        };

        recognition.onerror = (event) => {
            if (event.error === "no-speech") {
                if (stateRef.current === "listening") {
                    try { recognition.start(); } catch (_e) { /* retry */ }
                }
                return;
            }
        };

        recognition.onend = () => {
            if (stateRef.current === "listening" && recognitionRef.current === recognition) {
                try { recognition.start(); } catch (_e) { /* retry */ }
            }
        };

        try {
            recognition.start();
        } catch (_e) {
            toast.error("No se pudo activar el micrófono");
            setState("idle");
            setActive(false);
        }
    }, []); // cleanup on unmount

    const stopListening = () => {
        if (recognitionRef.current) {
            try { recognitionRef.current.abort(); } catch (_e) { /* cleanup */ }
            recognitionRef.current = null;
        }
    };

    // ── Chat + Sentence-based TTS ──
    const sendMessage = async (text) => {
        stopListening();
        setState("thinking");

        const token = getToken();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const resp = await fetch(`${API_BASE}/api/ai/jarvis/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ service_id: serviceId, message: text }),
                signal: controller.signal,
            });

            if (!resp.ok) throw new Error("Error del servidor");

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let fullText = "";
            let sentenceBuffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data: ")) continue;
                    const raw = line.slice(6).trim();
                    if (raw === "[DONE]") break;
                    try {
                        const parsed = JSON.parse(raw);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.content) {
                            fullText += parsed.content;
                            sentenceBuffer += parsed.content;

                            // Detect sentence end → send to TTS immediately
                            const sentenceMatch = sentenceBuffer.match(/^(.*?[.!?])\s*/);
                            if (sentenceMatch) {
                                const sentence = sentenceMatch[1];
                                sentenceBuffer = sentenceBuffer.slice(sentenceMatch[0].length);
                                enqueueTTS(sentence);
                            }
                        }
                    } catch (_e) { /* non-JSON SSE */ }
                }
            }

            // Flush remaining text
            if (sentenceBuffer.trim()) {
                enqueueTTS(sentenceBuffer.trim());
            }

            // If no text at all, go back to listening
            if (!fullText.trim()) {
                startListening();
            }
        } catch (e) {
            if (e.name !== "AbortError") {
                console.error("Jarvis error:", e);
                toast.error("Error comunicándose con Jarvis");
            }
            setState("idle");
            setActive(false);
        }
    };

    // ── Activate / Deactivate ──
    const handleActivate = async () => {
        if (active) {
            stopEverything();
            setState("idle");
            setActive(false);
            return;
        }

        setActive(true);
        setState("greeting");
        const token = getToken();

        try {
            const resp = await fetch(`${API_BASE}/api/ai/jarvis/greet/${serviceId}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!resp.ok) throw new Error("Error al saludar");

            const audioBytes = await resp.arrayBuffer();
            audioQueueRef.current.push(audioBytes);
            setState("speaking");
            playNextInQueue();
        } catch (_e) {
            toast.error("No se pudo activar Jarvis");
            setState("idle");
            setActive(false);
        }
    };

    const stateColors = {
        idle: "from-zinc-400 to-zinc-500",
        greeting: "from-cyan-400 to-blue-500",
        listening: "from-emerald-400 to-green-500",
        thinking: "from-amber-400 to-orange-400",
        speaking: "from-cyan-400 to-blue-500",
    };

    const stateLabels = {
        idle: "",
        greeting: "Conectando...",
        listening: "Escuchando...",
        thinking: "Pensando...",
        speaking: "Hablando...",
    };

    return (
        <div className="fixed bottom-20 left-5 z-[60]" data-testid="jarvis-button-container">
            {/* Status label */}
            {active && stateLabels[state] && (
                <div
                    className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur"
                    data-testid="jarvis-status"
                >
                    {stateLabels[state]}
                </div>
            )}

            {/* Animated aura rings */}
            {active && (
                <>
                    <div
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${stateColors[state]} opacity-25`}
                        style={{
                            animation: `ping ${state === "speaking" ? "0.8s" : state === "listening" ? "1.5s" : "2s"} cubic-bezier(0, 0, 0.2, 1) infinite`,
                        }}
                    />
                    <div
                        className={`absolute -inset-2 rounded-full bg-gradient-to-br ${stateColors[state]} opacity-15`}
                        style={{ animation: "pulse 1.5s ease-in-out infinite" }}
                    />
                    <div
                        className={`absolute -inset-4 rounded-full bg-gradient-to-br ${stateColors[state]} opacity-[0.07]`}
                        style={{ animation: "pulse 2.5s ease-in-out infinite" }}
                    />
                </>
            )}

            {/* Main button */}
            <button
                onClick={handleActivate}
                className={`relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
                    active
                        ? `bg-gradient-to-br ${stateColors[state]} shadow-xl scale-110`
                        : "bg-gradient-to-br from-zinc-700 to-zinc-900 hover:from-zinc-600 hover:to-zinc-800 hover:scale-105"
                }`}
                data-testid="jarvis-button"
                title={active ? "Desactivar Jarvis" : "Activar Jarvis"}
            >
                {active && <div className="absolute inset-1 rounded-full bg-white/10 backdrop-blur-sm" />}

                <svg
                    viewBox="0 0 24 24"
                    className={`w-7 h-7 relative z-10 transition-colors ${active ? "text-white" : "text-zinc-300"}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    {state === "listening" ? (
                        <>
                            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <line x1="12" x2="12" y1="19" y2="22" />
                        </>
                    ) : (
                        <>
                            <circle cx="12" cy="12" r="3" />
                            <path d="M12 2v4" />
                            <path d="M12 18v4" />
                            <path d="m4.93 4.93 2.83 2.83" />
                            <path d="m16.24 16.24 2.83 2.83" />
                            <path d="M2 12h4" />
                            <path d="M18 12h4" />
                            <path d="m4.93 19.07 2.83-2.83" />
                            <path d="m16.24 7.76 2.83-2.83" />
                        </>
                    )}
                </svg>
            </button>
        </div>
    );
}
